// Legacy Google Drive picker for pages that still push picked files into
// UploadModal (RFP Analyze, Projects, Account page, the old KB screen).
//
// The Knowledge Base has moved to `KnowledgeBase/useKbGoogleDrive.js`, where the
// backend downloads the bytes. This hook still downloads in the browser, so it
// carries the two fixes that flow needs:
//   * `response.ok` is checked — a 403 body must never become the "file".
//   * Google-native Docs/Sheets/Slides are pulled through `/export`, because
//     `?alt=media` returns 403 fileNotDownloadable for them.
import { useEffect, useState } from "react";
import api from "../chat/api";
import toast from "react-hot-toast";

// Native Drive types have no binary content — export them to something the
// upload pipeline understands.
const EXPORT_MAP = {
  "application/vnd.google-apps.document": {
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extension: "docx",
  },
  "application/vnd.google-apps.spreadsheet": {
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    extension: "xlsx",
  },
  "application/vnd.google-apps.presentation": {
    mimeType: "application/pdf",
    extension: "pdf",
  },
  "application/vnd.google-apps.drawing": {
    mimeType: "application/pdf",
    extension: "pdf",
  },
};

const withExtension = (name, extension) =>
  name.toLowerCase().endsWith(`.${extension}`) ? name : `${name}.${extension}`;

// Download one picked doc as a real File, or throw with a user-facing reason.
const fetchDriveFile = async (doc, accessToken) => {
  const exportAs = EXPORT_MAP[doc.mimeType];
  const url = exportAs
    ? `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=${encodeURIComponent(
        exportAs.mimeType
      )}`
    : `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&supportsAllDrives=true`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  // Without this check the error JSON body was silently uploaded as the file.
  if (!response.ok) {
    let reason = `${response.status}`;
    try {
      reason = (await response.json())?.error?.message || reason;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`${doc.name}: ${reason}`);
  }

  const blob = await response.blob();
  const name = exportAs
    ? withExtension(doc.name, exportAs.extension)
    : doc.name;
  return new File([blob], name, { type: blob.type || doc.mimeType });
};

export default function useGooglePicker(setFile = () => {}, setIsModalOpen = () => {}) {
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [isDriveConnected, setIsDriveConnected] = useState(false);

  // Load Google Picker SDK
  useEffect(() => {
    const loadPickerScript = () => {
      if (!window.google || !window.google.picker) {
        const script = document.createElement("script");
        script.src = "https://apis.google.com/js/api.js";
        script.onload = () => {
          window.gapi.load("picker", { callback: () => setPickerLoaded(true) });
        };
        document.body.appendChild(script);
      } else {
        setPickerLoaded(true);
      }
    };
    loadPickerScript();
  }, []);

  // Check backend connection
  const verifyDriveConnection = async () => {
    try {
      const res = await api.get("/api/v1/accounts/google-drive/picker-config/");
      setIsDriveConnected(!!res.data.connected);
    } catch (err) {
      console.error("Error verifying Drive connection:", err);
      setIsDriveConnected(false);
    }
  };

  // Initiate backend OAuth if not connected
  const handleConnectDrive = async () => {
    try {
      const res = await api.post("/api/v1/accounts/google-drive/");
      const authUrl = res.data.authorization_url;

      if (!authUrl) {
        toast.error("No authorization URL returned.");
        return;
      }

      const popupWidth = 600;
      const popupHeight = 700;
      const left = window.screen.width / 2 - popupWidth / 2;
      const top = window.screen.height / 2 - popupHeight / 2;

      const popup = window.open(
        authUrl,
        "GoogleDriveConnect",
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        toast.error("Please allow popups for this site.");
        return;
      }

      // Settle once, on whichever signal lands first. Previously a second
      // module-level listener fired too, double-toasting every connect.
      let settled = false;
      const finish = async (closePopup) => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", handleMessage);
        clearInterval(popupCheckInterval);
        try {
          if (closePopup && !popup.closed) popup.close();
        } catch {
          /* cross-origin popups may refuse close() */
        }
        await verifyDriveConnection();
      };

      const handleMessage = async (event) => {
        if (event.data?.service === "google_drive" && event.data?.connected) {
          await finish(true);
          toast.success("Google Drive connected successfully!");
        }
      };

      window.addEventListener("message", handleMessage);

      const popupCheckInterval = setInterval(() => {
        if (popup.closed) finish(false);
      }, 1000);
    } catch (err) {
      console.error("Error connecting to Google Drive:", err);
      toast.error("Failed to connect Google Drive.");
    }
  };

  // Open Google Picker
  const openPicker = async () => {
    try {
      const res = await api.get("/api/v1/accounts/google-drive/picker-config/");
      if (!res.data.connected) {
        setIsDriveConnected(false);
        toast.error("Please connect Google Drive first.");
        return;
      }

      const accessToken = res.data.access_token;
      if (!accessToken) {
        toast.error("Missing Google Drive access token. Please reconnect.");
        return;
      }

      if (!window.google || !window.google.picker) {
        toast.error("Google Picker not loaded yet.");
        return;
      }

      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setOwnedByMe(true);

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setDeveloperKey(res.data.developer_key)
        .setCallback(async (data) => {
          if (data.action !== window.google.picker.Action.PICKED) return;

          const selectedDocs = data.docs || [];
          const loadingToast = toast.loading(
            `Fetching ${selectedDocs.length} file(s) from Google Drive…`
          );

          // Settled results: one bad file must not sink the whole selection.
          const outcomes = await Promise.allSettled(
            selectedDocs.map((doc) => fetchDriveFile(doc, accessToken))
          );
          toast.dismiss(loadingToast);

          const fetchedFiles = outcomes
            .filter((o) => o.status === "fulfilled")
            .map((o) => o.value);
          outcomes
            .filter((o) => o.status === "rejected")
            .forEach((o) => toast.error(o.reason?.message || "Failed to fetch a Drive file."));

          if (!fetchedFiles.length) return;

          setFile(fetchedFiles);
          setIsModalOpen(true);
        })
        .build();

      picker.setVisible(true);
    } catch (err) {
      console.error("Error opening Google Picker:", err);
      toast.error("Failed to open Google Picker");
    }
  };

  return {
    isDriveConnected,
    pickerLoaded,
    verifyDriveConnection,
    handleConnectDrive,
    openPicker,
  };
}
